// Clinical definitions shared by the kiosk (immediate feedback) and the server (authoritative record).
// Thresholds here are a DRAFT for the Department's clinical governance to confirm before go-live
// (proposal Section 10 / 12). Nothing in this file produces a diagnosis.

export type VitalCode = 'height' | 'weight' | 'bmi' | 'bp_sys' | 'bp_dia' | 'pulse' | 'spo2' | 'temp';

export interface VitalDef {
  code: VitalCode;
  loinc: string;
  display: string;
  ur: string;
  unit: string; // UCUM
  unitLabel: string;
  decimals: number;
  // Physiological plausibility limits. A value outside them is rejected as a device/measurement fault.
  min: number;
  max: number;
}

export const VITALS: Record<VitalCode, VitalDef> = {
  height: { code: 'height', loinc: '8302-2', display: 'Body height', ur: 'قد', unit: 'cm', unitLabel: 'cm', decimals: 0, min: 40, max: 230 },
  weight: { code: 'weight', loinc: '29463-7', display: 'Body weight', ur: 'وزن', unit: 'kg', unitLabel: 'kg', decimals: 1, min: 2, max: 300 },
  bmi: { code: 'bmi', loinc: '39156-5', display: 'Body mass index', ur: 'باڈی ماس انڈیکس (BMI)', unit: 'kg/m2', unitLabel: 'kg/m²', decimals: 1, min: 8, max: 90 },
  bp_sys: { code: 'bp_sys', loinc: '8480-6', display: 'Systolic blood pressure', ur: 'بلڈ پریشر (اوپر والا)', unit: 'mm[Hg]', unitLabel: 'mmHg', decimals: 0, min: 50, max: 280 },
  bp_dia: { code: 'bp_dia', loinc: '8462-4', display: 'Diastolic blood pressure', ur: 'بلڈ پریشر (نیچے والا)', unit: 'mm[Hg]', unitLabel: 'mmHg', decimals: 0, min: 20, max: 180 },
  pulse: { code: 'pulse', loinc: '8867-4', display: 'Heart rate', ur: 'نبض', unit: '/min', unitLabel: '/min', decimals: 0, min: 20, max: 250 },
  spo2: { code: 'spo2', loinc: '59408-5', display: 'Oxygen saturation (SpO2)', ur: 'خون میں آکسیجن', unit: '%', unitLabel: '%', decimals: 0, min: 50, max: 100 },
  temp: { code: 'temp', loinc: '8310-5', display: 'Body temperature', ur: 'جسم کا درجہ حرارت', unit: 'Cel', unitLabel: '°C', decimals: 1, min: 30, max: 45 },
};

export const VITAL_CODES = Object.keys(VITALS) as VitalCode[];

export type VitalValues = Partial<Record<VitalCode, number>>;

// ---------------------------------------------------------------------------------------------
// Measurement quality (epic E04)
// ---------------------------------------------------------------------------------------------

export const QUALITY_ACCEPT = 0.7; // readings scored below this are repeated
export const MAX_ATTEMPTS = 3; // after this many poor readings the measurement is escalated to staff

export type QualityOutcome = 'accepted' | 'repeat' | 'escalate' | 'rejected_implausible';

export function assessReading(code: VitalCode, value: number, quality: number, attempt: number): QualityOutcome {
  const def = VITALS[code];
  if (!Number.isFinite(value) || value < def.min || value > def.max) {
    return attempt >= MAX_ATTEMPTS ? 'escalate' : 'rejected_implausible';
  }
  if (quality >= QUALITY_ACCEPT) return 'accepted';
  return attempt >= MAX_ATTEMPTS ? 'escalate' : 'repeat';
}

export function roundVital(code: VitalCode, value: number): number {
  const f = 10 ** VITALS[code].decimals;
  return Math.round(value * f) / f;
}

export function computeBmi(heightCm?: number, weightKg?: number): number | undefined {
  if (!heightCm || !weightKg) return undefined;
  const m = heightCm / 100;
  return roundVital('bmi', weightKg / (m * m));
}

// Unit normalisation for adapters that report in other units.
export function normaliseUnit(code: VitalCode, value: number, unit: string): number {
  const u = unit.trim().toLowerCase();
  if (code === 'temp' && (u === 'f' || u === '°f' || u === 'degf' || u === '[degf]')) return roundVital('temp', ((value - 32) * 5) / 9);
  if (code === 'weight' && (u === 'lb' || u === 'lbs' || u === '[lb_av]')) return roundVital('weight', value * 0.45359237);
  if (code === 'height' && (u === 'in' || u === '[in_i]')) return roundVital('height', value * 2.54);
  if (code === 'height' && u === 'm') return roundVital('height', value * 100);
  if ((code === 'bp_sys' || code === 'bp_dia') && u === 'kpa') return roundVital(code, value * 7.50062);
  return roundVital(code, value);
}

// ---------------------------------------------------------------------------------------------
// Triage thresholds (epic E06) — DRAFT, pending clinical governance sign-off
// ---------------------------------------------------------------------------------------------

export type Severity = 'urgent' | 'review' | 'info';

interface Band {
  rule: string;
  vital: VitalCode;
  op: '>=' | '<=' | '<' | '>';
  value: number;
  severity: Severity;
  en: string;
  ur: string;
}

export interface ThresholdSet {
  version: string;
  status: 'draft-pending-clinical-governance' | 'approved';
  approvedBy?: string;
  bands: Band[];
}

export const DEFAULT_THRESHOLDS: ThresholdSet = {
  version: 'AJK-DRAFT-0.1',
  status: 'draft-pending-clinical-governance',
  bands: [
    { rule: 'BP_SYS_CRISIS', vital: 'bp_sys', op: '>=', value: 180, severity: 'urgent', en: 'Very high blood pressure', ur: 'بلڈ پریشر بہت زیادہ ہے' },
    { rule: 'BP_DIA_CRISIS', vital: 'bp_dia', op: '>=', value: 120, severity: 'urgent', en: 'Very high blood pressure', ur: 'بلڈ پریشر بہت زیادہ ہے' },
    { rule: 'BP_SYS_LOW', vital: 'bp_sys', op: '<', value: 90, severity: 'urgent', en: 'Low blood pressure', ur: 'بلڈ پریشر کم ہے' },
    { rule: 'BP_SYS_HIGH', vital: 'bp_sys', op: '>=', value: 140, severity: 'review', en: 'Raised blood pressure', ur: 'بلڈ پریشر معمول سے زیادہ ہے' },
    { rule: 'BP_DIA_HIGH', vital: 'bp_dia', op: '>=', value: 90, severity: 'review', en: 'Raised blood pressure', ur: 'بلڈ پریشر معمول سے زیادہ ہے' },
    { rule: 'SPO2_CRITICAL', vital: 'spo2', op: '<', value: 90, severity: 'urgent', en: 'Low blood oxygen', ur: 'خون میں آکسیجن کم ہے' },
    { rule: 'SPO2_LOW', vital: 'spo2', op: '<', value: 94, severity: 'review', en: 'Blood oxygen below normal', ur: 'خون میں آکسیجن معمول سے کم ہے' },
    { rule: 'PULSE_VERY_HIGH', vital: 'pulse', op: '>', value: 130, severity: 'urgent', en: 'Very fast heart rate', ur: 'دل کی دھڑکن بہت تیز ہے' },
    { rule: 'PULSE_VERY_LOW', vital: 'pulse', op: '<', value: 40, severity: 'urgent', en: 'Very slow heart rate', ur: 'دل کی دھڑکن بہت آہستہ ہے' },
    { rule: 'PULSE_HIGH', vital: 'pulse', op: '>', value: 100, severity: 'review', en: 'Fast heart rate', ur: 'دل کی دھڑکن تیز ہے' },
    { rule: 'PULSE_LOW', vital: 'pulse', op: '<', value: 50, severity: 'review', en: 'Slow heart rate', ur: 'دل کی دھڑکن آہستہ ہے' },
    { rule: 'TEMP_VERY_HIGH', vital: 'temp', op: '>=', value: 39.5, severity: 'urgent', en: 'High fever', ur: 'تیز بخار' },
    { rule: 'TEMP_LOW', vital: 'temp', op: '<', value: 35, severity: 'urgent', en: 'Low body temperature', ur: 'جسم کا درجہ حرارت کم ہے' },
    { rule: 'TEMP_FEVER', vital: 'temp', op: '>=', value: 38, severity: 'review', en: 'Fever', ur: 'بخار' },
    { rule: 'BMI_UNDER', vital: 'bmi', op: '<', value: 18.5, severity: 'info', en: 'Weight below the healthy range', ur: 'وزن صحت مند حد سے کم ہے' },
    { rule: 'BMI_OBESE', vital: 'bmi', op: '>=', value: 30, severity: 'info', en: 'Weight well above the healthy range', ur: 'وزن صحت مند حد سے کافی زیادہ ہے' },
    { rule: 'BMI_OVER', vital: 'bmi', op: '>=', value: 25, severity: 'info', en: 'Weight above the healthy range', ur: 'وزن صحت مند حد سے زیادہ ہے' },
  ],
};

export interface Flag {
  rule: string;
  severity: Severity;
  vital?: VitalCode;
  value?: number;
  en: string;
  ur: string;
}

const SEVERITY_RANK: Record<Severity, number> = { urgent: 3, review: 2, info: 1 };

function matches(band: Band, v: number): boolean {
  switch (band.op) {
    case '>=': return v >= band.value;
    case '<=': return v <= band.value;
    case '>': return v > band.value;
    case '<': return v < band.value;
  }
}

/** Evaluates vitals against the threshold set. Only the most severe band per vital is reported. */
export function evaluateVitals(values: VitalValues, t: ThresholdSet = DEFAULT_THRESHOLDS): Flag[] {
  const best = new Map<string, Flag>();
  for (const band of t.bands) {
    const v = values[band.vital];
    if (v === undefined || !matches(band, v)) continue;
    // Systolic and diastolic share one "blood pressure" slot so a patient sees one message.
    const slot = band.vital === 'bp_dia' ? 'bp_sys' : band.vital;
    const prev = best.get(slot);
    if (!prev || SEVERITY_RANK[band.severity] > SEVERITY_RANK[prev.severity]) {
      best.set(slot, { rule: band.rule, severity: band.severity, vital: band.vital, value: v, en: band.en, ur: band.ur });
    }
  }
  return [...best.values()];
}

export type Priority = 'urgent' | 'review' | 'routine';
export type Route = 'urgent_review' | 'teleconsult' | 'queue';

export interface SymptomFlagInput {
  id: string;
  en: string;
  ur: string;
  redFlag: boolean;
  negated: boolean;
}

export interface TriageResult {
  thresholdVersion: string;
  priority: Priority;
  route: Route;
  flags: Flag[];
  /** Human-readable reasoning shown to staff and clinicians (proposal Section 07: "the system shows its reasoning"). */
  reasons: string[];
}

/**
 * Decision support only. Urgent results go to human review and cannot be dismissed by the patient;
 * any clinician override is recorded separately with a reason.
 */
export function triage(values: VitalValues, symptoms: SymptomFlagInput[] = [], t: ThresholdSet = DEFAULT_THRESHOLDS): TriageResult {
  const flags = evaluateVitals(values, t);
  const positive = symptoms.filter((s) => !s.negated);
  for (const s of positive) {
    if (s.redFlag) flags.push({ rule: `SYMPTOM_RED_FLAG:${s.id}`, severity: 'urgent', en: `Reported: ${s.en}`, ur: `بتایا گیا: ${s.ur}` });
  }
  const reasons: string[] = [];
  const urgent = flags.filter((f) => f.severity === 'urgent');
  const review = flags.filter((f) => f.severity === 'review');
  let priority: Priority = 'routine';
  let route: Route = 'queue';
  if (urgent.length) {
    priority = 'urgent';
    route = 'urgent_review';
    reasons.push(...urgent.map((f) => `URGENT ${f.rule}${f.value !== undefined ? ` (${f.value})` : ''}: ${f.en}`));
  } else if (review.length || positive.length) {
    priority = 'review';
    route = 'teleconsult';
    reasons.push(...review.map((f) => `REVIEW ${f.rule} (${f.value}): ${f.en}`));
    if (positive.length) reasons.push(`Symptoms reported: ${positive.map((s) => s.en).join(', ')}`);
  } else {
    reasons.push('All measured vitals within draft thresholds and no symptoms reported');
  }
  const missing = (['bp_sys', 'spo2', 'pulse', 'temp'] as VitalCode[]).filter((c) => values[c] === undefined);
  if (missing.length) reasons.push(`Not measured: ${missing.map((c) => VITALS[c].display).join(', ')}`);
  return { thresholdVersion: t.version, priority, route, flags, reasons };
}

// ---------------------------------------------------------------------------------------------
// Plain-language patient summary (journey step 08) — describes readings, never diagnoses.
// ---------------------------------------------------------------------------------------------

export type VitalStatus = 'normal' | 'high' | 'low' | 'attention';

export interface VitalLine {
  code: VitalCode;
  value: number;
  status: VitalStatus;
  en: string;
  ur: string;
}

export function describeVitals(values: VitalValues, t: ThresholdSet = DEFAULT_THRESHOLDS): VitalLine[] {
  const flags = evaluateVitals(values, t);
  const lines: VitalLine[] = [];
  for (const code of VITAL_CODES) {
    const value = values[code];
    if (value === undefined || code === 'bp_dia') continue;
    const flag = flags.find((f) => f.vital === code || (code === 'bp_sys' && f.vital === 'bp_dia'));
    let status: VitalStatus = 'normal';
    if (flag) {
      const band = t.bands.find((b) => b.rule === flag.rule)!;
      status = band.op.startsWith('<') ? 'low' : 'high';
      if (flag.severity === 'urgent') status = 'attention';
    }
    const display = code === 'bp_sys' && values.bp_dia !== undefined ? `${value}/${values.bp_dia}` : String(value);
    lines.push({
      code,
      value,
      status,
      en: `${code === 'bp_sys' ? 'Blood pressure' : VITALS[code].display}: ${display} ${VITALS[code].unitLabel}${flag ? ` — ${flag.en}` : ' — within the usual range'}`,
      // LRI…PDI keeps "86 /min" and "36.5 °C" in left-to-right order inside Urdu text.
      ur: `${code === 'bp_sys' ? 'بلڈ پریشر' : VITALS[code].ur}: \u2066${display} ${VITALS[code].unitLabel}\u2069${flag ? ` — ${flag.ur}` : ' — معمول کے مطابق'}`,
    });
  }
  return lines;
}
